<template>
  <block-item-card
    :min-height="minBlockHeight"
    class="gradient-grey-06-bg"
  >
    <v-row
      class="ma-0 py-4 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <div class="chart-container">
          <div class="chart-header">
            <div class="d-flex flex-column">
              <div class="d-flex">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-grey-01-base);"
                >
                  {{ $t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Title') }}
                </h3>
                <infotip
                  icon-style="margin-top: 2px;margin-left: 4px;"
                >
                  {{ $t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Tooltip') }}
                </infotip>
              </div>
              <div class="d-flex">
                <asset-button-group
                  :height="26"
                  :items="marketPriceActionFilterItems"
                  :selected.sync="selectedFilter"
                  disable-dropdown-on-mobile
                  style="margin-top: 10px !important;text-align: left !important;"
                />
              </div>
            </div>

            <div class="chart-metric">
              <span class="metric-label">{{ $t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Change') }}</span>
              <span :class="['metric-value', change >= 0 ? 'positive' : 'negative']">
                {{ change }}
              </span>
            </div>
          </div>
          <v-lazy>
            <e-chart
              :ref="`echart-market-price-action`"
              :autoresize="true"
              :option="marketPriceActionChartOptions"
              :style="`height: 250px;`"
              class="chart"
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

      <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import Infotip from '@/theme/default/components/common/Infotip'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import API from '@/api/Api'
import Auth from '@/mixins/auth'
import { clone, toNumberFormatNotationShort, computeYAxisBounds }                         from '@/lib/utils/helper'
import { MarkLineComponent } from 'echarts/components'

import { use } from 'echarts/core'
use([MarkLineComponent])

export default {
  name      : 'MarketPriceAction',
  components: {
    BlockItemCard,
    AssetButtonGroup,
    Infotip
  },
  dataStore: {
    MarketPriceAction: 'RiskLiquidity.MarketPriceAction'
  },
  mixins: [chartsColorMixin, Auth],
  props : {},
  data () {
    return {
      selectedFilter              : '6M',
      minBlockHeight              : 350,
      marketPriceActionFilterItems: [
        {
          Title: this.$t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Filters.Last7Days'),
          Value: '7D'
        },
        {
          Title: this.$t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Filters.Last30Days'),
          Value: '30D'
        },
        {
          Title: this.$t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Filters.Last6Months'),
          Value: '6M'
        }
      ]
    }
  },
  computed: {
    thresholdMarkLines () {
      const list = this.marketPriceAction?.thresholds || []

      return list.map(t => ({
        yAxis: Number(t.threshold),
        name : t.title,

        lineStyle: {
          color: this.themeColorRGBA('synthesis-ui-red-02', 1),
          width: 2,
          type : 'dashed'
        },

        label: {
          show           : true,
          position       : 'end',
          formatter      : p => p?.data?.name || '',
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-02', 1),
          borderWidth    : 1,
          color          : this.themeColorRGBA('synthesis-ui-red-02', 1),
          distance       : 2,
          fontWeight     : 500,
          fontSize       : 14,
          fill           : this.themeColorRGBA('synthesis-ui-orange-02'),
          textAlign      : 'center',
          backgroundColor: '#FFFFFF',
          borderRadius   : 4,
          padding        : [4, 10],
          shadowBlur     : 22,
          shadowOffsetX  : 0,
          shadowOffsetY  : 6,
          shadowColor    : 'rgba(192, 206, 220, 1)'
        }
      }))
    },
    marketPriceAction () {
      return this.MarketPriceAction.Data
    },
    change () {
      return this.marketPriceAction?.change + '%' || '0%'
    },
    formattedDates () {
      if (!this.marketPriceAction?.items) return []
      return this.marketPriceAction.items.map(item => {
        const d = new Date(item.date)
        const day = d.getDate().toString().padStart(2, '0')
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString().slice(-2)
        return `${day} ${month} ${year}`
      })
    },

    selectedDate () {
      const date = new Date()

      switch (this.selectedFilter) {
        case '7D':
          date.setDate(date.getDate() - 7)
          break
        case '30D':
          date.setDate(date.getDate() - 30)
          break
        case '6M':
        default:
          date.setMonth(date.getMonth() - 6)
          break
      }
      return date.toISOString().split('T')[0]
    },

    yAxisMin () {
      const opts = { padRatio: 0 }

      switch (this.selectedFilter) {
        case '6M':
          opts.padRatio = 0.2
          break
        case '30D':
          opts.padRatio = 0.02
          break
        default:
          opts.padRatio = 0.002
          break
      }

      return computeYAxisBounds(this.marketPriceAction.items?.map(item => item.value) || []).min
    },
    yAxisMax () {
      const opts = { padRatio: 0 }

      switch (this.selectedFilter) {
        case '6M':
          opts.padRatio = 0.05
          break
        case '30D':
          opts.padRatio = 0.05
          break

        default:
          opts.padRatio = 0.005
          break
      }

      const list = this.marketPriceAction?.thresholds || []
      const maxThreshold = list.length
        ? Math.max(...list.map(item => Number(item.threshold)))
        : null

      const maxValue = computeYAxisBounds(this.marketPriceAction.items?.map(item => item.value) || []).max

      if (maxThreshold === null) return maxValue
      return Math.max(maxThreshold, maxValue)
    },

    marketPriceActionChartOptions () {
      if (!this.marketPriceAction) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value = params[0].value
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                        <span>${this.$t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.MarketPriceAction.Title')}: ${toNumberFormatNotationShort(value)}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 50,
          right       : 50,
          bottom      : 0,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type       : 'value',
          min        : this.yAxisMin,
          max        : this.yAxisMax,
          interval   : computeYAxisBounds(this.marketPriceAction.items?.map(item => item.value) || []).interval,
          splitNumber: 4,
          axisLabel  : {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            showMaxLabel : true,
            formatter    : (value) => {
              return toNumberFormatNotationShort(value)
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Market Value',
          type     : 'line',
          data     : this.marketPriceAction.items?.map(item => item.value) || [],
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-orange-02')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 0,
          markLine  : {
            symbol   : 'none',
            silent   : true,
            animation: false,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-red-01'),
              type : 'dashed',
              width: 1
            },
            label: {
              show     : true,
              position : 'end', // put label at the right end of the line
              formatter: (p) => p?.data?.name || '',

              // “div-like” styling
              padding        : [2, 6],
              backgroundColor: '#FFFFFF',
              borderColor    : this.themeColorRGBA('synthesis-ui-grey-02', 1),
              borderWidth    : 1,
              borderRadius   : 4,
              color          : this.themeColorRGBA('synthesis-ui-grey-01', 1),
              fontSize       : 10
            },
            data: this.thresholdMarkLines
          }
        }]
      }
    },
    accountId () {
      return this.user?.selectedAccountId || null
    }
  },

  watch: {
    selectedFilter: {
      handler () {
        this.getMarketPriceAction()
      }
    }
  },
  mounted () {},
  created () {
    this.getMarketPriceAction()
  },
  methods: {
    async getMarketPriceAction () {
      const response = await API.Resource.RiskLiquidity.GetMarketPriceAction(this.accountId, this.selectedDate)
      this.handleResponse(response)
    },
    handleResponse (response) {
      const data = API.responseData(response)?.marketPriceAction || null

      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        this.MarketPriceAction.Data = clone(data)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    }
  }
}
  </script>

  <style scoped>

.gradient-grey-06-bg {
  background-color: var(--v-synthesis-ui-grey-06-base) !important;
}

.chart-title {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.chart-subtitle {
  margin-left: 8px;
  color: var(--v-synthesis-ui-grey-01-base);
  font-size: 10px;
  position: relative;
  top: 1px;
}

.metric-label {
  font-size: 10px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 8px;
}

.chart-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.metric-value {
  font-size: 12px;
  font-weight: 500;
}

.metric-value.positive {
  color: #4CAF50;
  font-size: 28px;
}

.metric-value.negative {
  color: #FF5722;
  font-size: 28px;
}

.chart-legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #666;
}

.legend-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
  </style>
